import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Music, Gamepad2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SoberSocialGuide() {
  const navigate = useNavigate();

  const socialIdeas = [
    { icon: Music, title: "Morning activities", desc: "Coffee dates, brunch, breakfast meetups, morning walks" },
    { icon: Gamepad2, title: "Activity-based", desc: "Hiking, bowling, escape rooms, cooking classes, game nights" },
    { icon: MessageCircle, title: "Home gatherings", desc: "Potlucks with mocktails, movie nights, craft evenings" },
    { icon: Users, title: "New communities", desc: "Book clubs, fitness classes, volunteer groups, hobby meetups" }
  ];

  const phrases = [
    "\"I'm not drinking tonight\" (no explanation needed)",
    "\"I'm doing a wellness challenge this month\"",
    "\"I'm on a cleanse\" (people rarely question this)",
    "\"I'm driving\" (simple and understood)",
    "\"I have an early morning\" (true or not)",
    "\"I just don't feel like it tonight\""
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-100 rounded-full mb-4">
            <Users className="w-8 h-8 text-violet-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Sober Social Activity</h1>
          <p className="text-lg text-secondary-600">Enjoy socializing without alcohol being the focus</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Connection vs. Drinking</h2>
          <p className="text-secondary-700 mb-4">
            We often conflate socializing with drinking, but what we really crave is connection. Today, you'll prove to yourself that you can have fun, be social, and feel connected without alcohol being the centerpiece.
          </p>
          <div className="bg-violet-50 rounded-xl p-4 border border-violet-200">
            <p className="text-violet-800 font-medium">
              🎯 The goal isn't to avoid social situations—it's to reclaim them. You can be the fun, engaging person you are without alcohol.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Social Ideas That Don't Center Alcohol</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {socialIdeas.map((idea, i) => (
              <div key={i} className="p-4 bg-violet-50 rounded-xl border border-violet-200">
                <div className="flex items-center gap-3 mb-2">
                  <idea.icon className="w-6 h-6 text-violet-600" />
                  <h3 className="font-bold text-secondary-900">{idea.title}</h3>
                </div>
                <p className="text-secondary-600 text-sm">{idea.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 border-2 border-violet-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">If Someone Asks Why You're Not Drinking</h2>
          <p className="text-secondary-600 mb-4">You owe no one an explanation, but if you want one ready:</p>
          <div className="space-y-2">
            {phrases.map((phrase, i) => (
              <div key={i} className="p-3 bg-white rounded-lg">
                <span className="text-secondary-700">{phrase}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-violet-100 rounded-lg">
            <p className="text-violet-800 text-sm font-medium">
              💡 Remember: Most people are too focused on themselves to care what's in your glass. Confidence is contagious.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Today's Challenge</h2>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="bg-violet-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <span className="text-secondary-700">Plan or attend a social activity today (even a phone call counts)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-violet-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <span className="text-secondary-700">Keep it alcohol-free—have a mocktail or other drink ready</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-violet-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <span className="text-secondary-700">Focus on connection—ask questions, really listen</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-violet-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <span className="text-secondary-700">Notice how you feel during and after</span>
            </li>
          </ol>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "What social activity did I do today? How did it feel to be fully present without alcohol? What did I notice about my connections?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default SoberSocialGuide;
